<template>
  <div :class="$style.container">
    <div
      :class="$style.content"
    >
      <h5 :class="$style.contentHeader">
        Event Type: {{ eventTypeName }}
      </h5>
      <h6
        v-if="subheader.text"
        :class="$style.contentSubheader"
      >
        <component
          :is="subheader.icon"
          v-if="subheader.icon"
          :class="$style.subheaderIcon"
        />
        {{ subheader.text }}
      </h6>
      <div :class="$style.centered">
        <div :class="$style.mainSummaryContent">
          <div
            v-if="icons.left && icons.right"
            :class="$style.iconContainer"
          >
            <div :class="$style.iconContent">
              <!--
                RR-658 Need to investigate a better alternative to dynamic components in order
                to test against the rendered icons versus component props.
                @see https://jumpcloud.atlassian.net/browse/RR-658
                -->
              <component
                :is="icons.left"
                :class="$style.circleIcon"
                :data-test-id="$testId('leftCircleIcon')"
              />
              <component
                :is="icons.arrow"
                :class="$style.arrowIcon"
                :data-test-id="$testId('leftArrowIcon')"
              />
              <component
                :is="icons.center"
                :class="$style.circleIcon"
                :data-test-id="$testId('centerCircleIcon')"
              />
              <component
                :is="icons.arrow"
                :class="$style.arrowIcon"
                :data-test-id="$testId('rightArrowIcon')"
              />
              <component
                :is="icons.right"
                :class="$style.circleIcon"
                :data-test-id="$testId('rightCircleIcon')"
              />
            </div>
          </div>
          <div
            :class="$style.summaryText"
          >
            {{ summaryText }}
          </div>
          <div
            v-if="summaryTextSecondary"
            :class="$style.summaryTextSecondary"
          >
            {{ summaryTextSecondary }}
          </div>
        </div>
      </div>
      <div :class="$style.miniCardContainer">
        <div
          v-for="(card, i) in miniCards"
          :key="card.name"
          :class="$style.miniCard"
          :data-test-id="$testId('miniCard' + i)"
        >
          <h6
            :class="$style.miniCardHeader"
          >
            {{ card.name }}
          </h6>
          <div
            v-for="(content, index) in card.content"
            :key="index"
          >
            {{ content || '-' }}
          </div>
        </div>
      </div>
      <div
        v-if="eventDetails.length > 0"
        :class="$style.cardDetails"
      >
        <HeadingExtraSmall>
          Event Details
        </HeadingExtraSmall>
        <Grid>
          <GridItem
            v-for="(detail, index) in eventDetails"
            :key="detail.title"
            :class="$style.cardDetailsItem"
            :gridSpan="4"
          >
            <h6
              :class="$style.miniCardHeader"
              :data-test-id="$testId('cardDetailsItemHeader' + index)"
            >
              {{ detail.title }}
            </h6>
            <p :data-test-id="$testId('cardDetailsItemParagraph' + index)">
              {{ detail.text || '-' }}
            </p>
          </GridItem>
        </Grid>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Grid,
  GridItem,
  HeadingExtraSmall,
} from '@jumpcloud/ui-components';
import { upperFirst } from 'lodash';
import EventMetadata, {
  eventFields, eventServiceMetadata, eventTypeMetadata,
} from 'client/Insights/ActivityLog/constants/EventMetadata';
import EventTypes, { eventTypeNameMap } from 'client/Insights/ActivityLog/constants/EventTypes';
import mfaIcon from 'public/img/activityLogSummary/mfa.svg?inline';
import organizationIcon from 'public/img/activityLogSummary/organization.svg?inline';
import userIconNoCircle from 'public/img/activityLogSummary/user-no-circle.svg?inline';

export default {
  name: 'EventSummary',
  components: {
    HeadingExtraSmall,
    Grid,
    GridItem,
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
  },
  computed: {
    eventDetails() {
      const details = [];
      if (this.record.initiated_by?.source) {
        details.push({
          title: 'Source',
          text: this.record.initiated_by.source,
        });
      }

      if (this.record.correlation?.id) {
        details.push(
          {
            title: 'Correlation',
            text: this.record.correlation.id,
          },
        );
      }

      if (this.record.initiated_by?.source_metadata?.name) {
        details.push(
          {
            title: 'Source Metadata',
            text: this.record.initiated_by.source_metadata.name,
          },
        );
      }

      const eventType = eventTypeMetadata[this.record.event_type];

      // Add custom Event Details minicard when designated.
      if (typeof eventType?.customMiniCardLayout === 'number') {
        details.push(
          {
            title: 'Resource Type',
            text: this.record.resource?.type || '-',
          },
        );

        switch (eventType.customMiniCardLayout) {
          case 1:
            details.push({
              title: 'Resource Name',
              text: this.record.resource?.name || '-',
            });
            break;
          case 2:
            details.push({
              title: 'Resource HostName',
              text: this.record.resource?.hostname || '-',
            });
            break;
          case 4:
            details.push(
              {
                title: 'Resource Name',
                text: this.record.resource?.name || '-',
              },
              {
                title: 'Resource Recipient Email',
                text: this.record.resource?.recipient_email || '-',
              },
            );
            break;
          case 5:
            details.push(
              {
                title: 'Resource User Name',
                text: this.record.resource?.username || '-',
              },
              {
                title: 'Application Name',
                text: this.record.application?.name || '-',
              },
            );
            break;
          case 6:
            details.push(
              {
                title: 'Resource User Name',
                text: this.record.resource?.username || '-',
              },
              {
                title: 'Device Hostname',
                text: this.record.system?.hostname || '-',
              },
            );
            break;
          case 7:
            details.push(
              {
                title: 'Resource User Name',
                text: this.record.resource?.username || '-',
              },
              {
                title: 'Error Message',
                text: this.record.error_message || '-',
              },
            );
            break;
          case 8:
            details.push(
              {
                title: 'Error Message',
                text: this.record.error_message || '-',
              },
            );
            break;
          default:
            break;
        }
      }

      return details;
    },

    eventTypeName() {
      const name = eventTypeNameMap[this.record.event_type];
      if (!name) return this.record.event_type;
      return name.split(' ').map(word => upperFirst(word)).join(' ');
    },

    icons() {
      const { record } = this;
      const eventType = eventTypeMetadata[record.event_type];

      if (!eventType) {
        return eventServiceMetadata.all.icons;
      }

      // Override default event service icons if custom icons are designated.
      if (typeof eventType.customIcons === 'function') return eventType.customIcons(record);

      return eventServiceMetadata[eventType.service]?.icons;
    },

    miniCards() {
      const eventType = this.record.event_type;

      const timestampCard = {
        name: 'Timestamp',
        content: [
          EventMetadata[eventFields.timestamp].getDataValue(this.record),
        ],
      };

      const initiatedByCard = {
        name: 'Initiated By',
        content: [
          EventMetadata[eventFields.initiatedBy].getDataValue(this.record),
        ],
      };

      const locationCard = [{
        name: 'Location',
        content: [
          ...(this.record.client_ip ? [`${this.record.client_ip} (client IP)`] : []),
          ...(this.record.src_ip ? [`${this.record.src_ip} (source IP)`] : []),
          ...(this.record.src_geoip
            ? [EventMetadata[eventFields.geoipSource].getDataValue(this.record)]
            : [EventMetadata[eventFields.geoip].getDataValue(this.record)]),
        ],
      }];

      const deviceCard = {
        name: 'Device',
        content: [
          this.record.system?.displayName || this.record.system?.hostname
          || this.record.resource?.displayName || this.record.resource?.hostname,
          this.record.system?.id || this.record.resource?.id,
        ],
      };

      const defaultCards = [
        timestampCard,
        initiatedByCard,
        ...(this.record.client_ip || this.record.src_ip ? locationCard : []),
      ];

      if (eventType === EventTypes.associationChange) {
        const results = {
          name: 'Results',
          content: [
            this.summaryText,
          ],
        };
        defaultCards.splice(1, 0, results);
        return defaultCards;
      }

      if (eventType === EventTypes.loginAttempt) {
        return [
          ...defaultCards,
          deviceCard,
        ];
      }

      const isShowDeviceCard = eventType === EventTypes.systemFdeKeyDecrypt
        || eventType === EventTypes.systemFdeKeyUpdate
        || eventType === EventTypes.remoteSessionStart
        || eventType === EventTypes.remoteSessionEnd
        || eventType === EventTypes.remoteSessionJoin
        || eventType === EventTypes.remoteSessionFeedback
        || eventType === EventTypes.softwareAdd
        || eventType === EventTypes.softwareChange
        || eventType === EventTypes.softwareRemove;

      if (isShowDeviceCard) {
        return [
          timestampCard,
          deviceCard,
          ...(this.record.client_ip || this.record.src_ip ? locationCard : []),
        ];
      }

      const isOsEvent = eventType === EventTypes.osMajorUpgrade
        || eventType === EventTypes.osMajorRollback
        || eventType === EventTypes.osMinorRollback
        || eventType === EventTypes.osMinorUpgrade;

      if (isOsEvent) {
        return [
          timestampCard,
          deviceCard,
          {
            name: 'From',
            content: [
              this.record.changes?.length ? this.record.changes[0].from : '-',
            ],
          },
          {
            name: 'To',
            content: [
              this.record.changes?.length ? this.record.changes[0].to : '-',
            ],
          },
        ];
      }

      return [
        ...defaultCards,
      ];
    },

    subheader() {
      const { record } = this;
      switch (record.event_type) {
        case EventTypes.adminLoginAttempt:
        case EventTypes.userLoginAttempt:
          return {
            icon: record.mfa ? mfaIcon : null,
            text: `TOTP MFA ${record.mfa ? 'on' : 'off'}`,
          };
        case EventTypes.invoiceDownload:
          return {
            icon: null,
            text: 'Resource Type: Invoices',
          };
        case EventTypes.organizationUpdate:
          return {
            icon: organizationIcon,
            text: 'Resource Type: Organization',
          };
        case EventTypes.userActivated:
        case EventTypes.userDelete:
        case EventTypes.userLockout:
        case EventTypes.userPasswordChange:
        case EventTypes.userPasswordExpired:
        case EventTypes.userPasswordSet:
        case EventTypes.userSuspended:
        case EventTypes.userUnlocked:
          return {
            icon: userIconNoCircle,
            text: 'Resource Type: User',
          };
        default:
          return {};
      }
    },

    /**
     * This computed property is the main summary text shown to the admin.
     * It is similar to the "Result" column value, but in the form of a full sentence.
     */
    summaryText() {
      const eventTypeIconViewSummary = eventTypeMetadata[this.record.event_type]?.summary;

      return eventTypeIconViewSummary
        ? eventTypeIconViewSummary(this.record)
        : '';
    },

    summaryTextSecondary() {
      const { record } = this;
      switch (record.event_type) {
        case EventTypes.organizationUpdate:
        case EventTypes.userUpdate:
          if (record.changes.length > 1) {
            return record.changes.map(d => d.field).join(', ');
          }
          return '';
        default:
          return '';
      }
    },
  },
};
</script>

<style module>
@value (gradientLight2, gradientLight4) from '~css/colors.css';
@value lightShadow2 from '~css/shadows.css';

.container {
  background-color: #f5f5f5; /* using same color as the <pre> element */
  padding: 20px;
}

.content {
  background-color: white;
  border: 1px solid gradientLight2;
  box-shadow: lightShadow2;
  padding: 20px;
}

.contentHeader {
  font-weight: bold !important; /* overriding (.jc-ux2 h5) legacy styles */
  margin: 0 0 8px !important; /* overriding (.jc-ux2 h5) legacy styles */
}

.contentSubheader {
  align-items: flex-end;
  display: flex;
  margin-top: 8px !important;
}

.subheaderIcon {
  height: 13px;
  margin-right: 6px;
  width: 13px;
}

.mainSummaryContent {
  display: inline-block;
  max-width: 700px;
  padding: 15px 0 30px;
}

.iconContainer { text-align: center; }

.iconContent {
  display: inline-flex;
  justify-content: space-between;
  margin-bottom: 20px;
  position: relative;
  width: 300px;
}

.arrowIcon,
.circleIcon {
  height: 58px;
  width: 58px;
}

.summaryText {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.summaryTextSecondary {
  font-size: 13px;
  margin-top: 5px;
  text-align: center;
}

.miniCardContainer {
  display: flex;
}

.miniCard {
  border: 1px solid gradientLight4;
  box-shadow: lightShadow2;
  flex: 1;
  padding: 10px 20px;
}

.miniCard:nth-child(n+2) { margin-left: 20px; }
.miniCardHeader { margin: 0 0 5px !important; }
.centered { text-align: center; }

.cardDetails {
  border: 1px solid gradientLight4;
  box-shadow: lightShadow2;
  margin: 10px 0;
  padding: 10px 20px;
}

.cardDetailsItem:not(:nth-child(3n+1)) {
  margin-left: 20px;
}
</style>
